import React, { useState, useEffect } from 'react';
import { CheckCircle2, Clock, Users, ShieldCheck, FileCheck, RefreshCw } from 'lucide-react';
import { docDistributionService, DocumentDistributionRecord } from '../docDistributionService';
import { DocumentRecord } from '../types';

interface DocumentComplianceAuditTabProps {
  documents: DocumentRecord[];
}

export const DocumentComplianceAuditTab: React.FC<DocumentComplianceAuditTabProps> = ({ documents }) => {
  const [selectedDocId, setSelectedDocId] = useState<string>(documents[0]?.id || '');
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedDocId) {
      loadReport(selectedDocId);
    }
  }, [selectedDocId]);

  const loadReport = async (docId: string) => {
    setLoading(true);
    try {
      const data = await docDistributionService.getComplianceReport(docId);
      setReport(data);
    } catch (e) {
      console.error('Failed to load compliance report:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Selector & KPI Banner */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <FileCheck size={18} className="text-indigo-600" />
            Mandatory Document Distribution & Read-and-Sign Compliance Audit
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Audit-grade tracking of digital signatures, IP timestamps, and target audience compliance rates
          </p>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-xs font-semibold text-slate-600">Select Document:</label>
          <select
            value={selectedDocId}
            onChange={(e) => setSelectedDocId(e.target.value)}
            className="text-xs border border-slate-300 rounded-lg px-3 py-1.5 bg-white text-slate-800 font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.document_number} — {d.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      {report && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-xs font-medium text-slate-500">Compliance Rate</div>
            <div className="text-2xl font-bold text-indigo-600 mt-1">
              %{report.compliance_rate_percent || 0}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Target audience acknowledged</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-xs font-medium text-slate-500">Target Personnel</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">
              {report.total_target_users || 0}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Total required signees</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-xs font-medium text-slate-500">Acknowledged & Signed</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">
              {report.total_acknowledged_users || 0}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Verified digital signatures</div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-xs font-medium text-slate-500">Pending / Overdue</div>
            <div className="text-2xl font-bold text-rose-600 mt-1">
              {report.overdue_count || 0}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">Urgent follow-up required</div>
          </div>
        </div>
      )}

      {/* Detailed Signatures Audit Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider flex justify-between items-center">
          <span>Digital Signature Audit Evidence Log</span>
          <span className="text-[11px] font-normal text-slate-400 lowercase">SHA-256 tamper-proof verification</span>
        </div>

        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase font-semibold">
            <tr>
              <th className="py-3 px-4">Signee Personnel</th>
              <th className="py-3 px-4">Department</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Signed Timestamp</th>
              <th className="py-3 px-4">IP Address & User-Agent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-slate-400">Loading digital signature evidence...</td>
              </tr>
            ) : !report?.audit_trail || report.audit_trail.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-slate-400">No signature records found for this document yet.</td>
              </tr>
            ) : (
              report.audit_trail.map((sig: any, idx: number) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="py-3 px-4 font-semibold text-slate-800">
                    {sig.user_name || sig.user_id}
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    {sig.department || 'Operations'}
                  </td>
                  <td className="py-3 px-4">
                    {sig.acknowledged_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full text-[11px]">
                        <CheckCircle2 size={12} /> Signed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded-full text-[11px]">
                        <Clock size={12} /> Pending
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-600">
                    {sig.acknowledged_at ? new Date(sig.acknowledged_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-3 px-4 font-mono text-slate-500 text-[11px]">
                    {sig.ip_address || '127.0.0.1'} {sig.user_agent ? `(${sig.user_agent.slice(0, 25)}...)` : ''}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
